import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CaretDown, GlobeHemisphereWest, MagnifyingGlass, UserCircle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ErrorBoundary } from "./ErrorBoundary";
import { GlobalSearchDialog } from "./GlobalSearchDialog";

const navItems = [
  ["/boards", "boards"],
  ["/videos", "videos"],
  ["/workflows", "workflows"],
  ["/legal", "legal"],
] as const;

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { profile, signOut, demoMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const language = i18n.resolvedLanguage?.startsWith("en") ? "en" : "bg";
  const setLanguage = (nextLanguage: "bg" | "en") => {
    localStorage.setItem("pactios-language", nextLanguage);
    document.documentElement.lang = nextLanguage;
    void i18n.changeLanguage(nextLanguage);
  };
  const nextLanguage = language === "bg" ? "en" : "bg";

  return (
    <div className="app-shell">
      <header className="masthead">
        <NavLink className="wordmark" to="/workspace" aria-label={t("shell.home")} title={t("shell.home")}>
          <img src="/assets/logo-black.svg" alt="" />
        </NavLink>
        <nav className="primary-nav" aria-label={t("shell.navigation")}>
          {navItems.map(([to, key]) => (
            <NavLink key={to} to={to} className={({ isActive }) => isActive ? "active" : undefined}>
              {t(`nav.${key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="masthead-actions">
          <button className="search-trigger" type="button" onClick={() => setSearchOpen(true)} aria-label={t("search.label")}>
            <MagnifyingGlass size={18} weight="regular" />
            <span className="search-trigger-label">{t("search.label")}</span>
            <kbd>Ctrl K</kbd>
          </button>
          <button
            className="language-toggle"
            type="button"
            onClick={() => setLanguage(nextLanguage)}
            aria-label={t(language === "bg" ? "shell.switchToEnglish" : "shell.switchToBulgarian")}
            title={t(language === "bg" ? "shell.switchToEnglish" : "shell.switchToBulgarian")}
          >
            <GlobeHemisphereWest size={19} />
            <span>{nextLanguage.toUpperCase()}</span>
          </button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger className="account-trigger" aria-label={t("nav.account")}>
              <span className="account-name"><strong>{profile?.displayName}</strong><small>{t(demoMode ? "shell.demoAdministrator" : "shell.administrator")}</small></span>
              <CaretDown size={16} />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="menu-content" align="end" sideOffset={8}>
                <DropdownMenu.Item className="menu-item" onSelect={() => navigate("/account")}><UserCircle size={17} /> {t("nav.account")}</DropdownMenu.Item>
                <DropdownMenu.Separator className="menu-separator" />
                <DropdownMenu.Item className="menu-item" onSelect={() => void signOut().then(() => navigate("/login"))}>{t("shell.signOut")}</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </header>
      {/* Keyed by route so navigating away from a failed module clears the error. */}
      <main className="app-main"><ErrorBoundary key={location.pathname}><Outlet /></ErrorBoundary></main>
      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
